import { el } from "./dom.js";
import { statusLabel } from "./status-badge.js";
import { summarizeMembers } from "../db/groups.js";

const PLACEHOLDER = "/placeholder-cover.svg";

// A group rendered as a row that looks just like a single item entry (cover,
// title, subtitle, status) so groups can sit inline alongside games/books/TV in
// the same list. `members` is the group's resolved, normalized member list (from
// db/media.js's shape) — the caller resolves it so this component stays sync and
// free of DB imports.

// Map a group's media_type scope to the token statusBadge/statusLabel want for
// media-aware wording. "videogame" uses the default (game) labels → null.
function labelMediaType(group) {
	if (group.media_type === "book") return "book";
	if (group.media_type === "tv") return "tv";
	if (group.media_type === "anime") return "anime";
	if (group.media_type === "movie") return "movie";
	if (group.media_type === "comic") return "comic";
	return null;
}

// Cover art for a group: the explicitly chosen member (cover_item_id) if it
// still resolves, else the first member that has art, else the placeholder.
export function groupCoverUrl(group, members) {
	if (group.cover_item_id) {
		const chosen = members.find((m) => m.id === group.cover_item_id);
		if (chosen?.cover_url) return chosen.cover_url;
	}
	return members.find((m) => m.cover_url)?.cover_url || PLACEHOLDER;
}

// How many member names to spell out before collapsing the rest into an ellipsis.
const SUBTITLE_MAX = 3;

// Subtitle line: the contributors inside the group. Books carry authors, so a
// 40k group reads "Dan Abnett, Aaron Dembski-Bowden, …". Media without authors
// (games/TV) fall back to member titles so the line still says what's inside.
export function groupSubtitle(members) {
	const authors = [];
	const seen = new Set();
	for (const m of members) {
		for (const a of m.authors ?? []) {
			if (!seen.has(a)) {
				seen.add(a);
				authors.push(a);
			}
		}
	}
	const names =
		authors.length > 0 ? authors : members.map((m) => m.title).filter(Boolean);
	if (names.length === 0) return "Empty group";
	const shown = names.slice(0, SUBTITLE_MAX).join(", ");
	return names.length > SUBTITLE_MAX ? `${shown}…` : shown;
}

export function groupCard(group, members, { onClick } = {}) {
	const cover = el("img.game-row__cover", {
		src: groupCoverUrl(group, members),
		alt: "",
		loading: "lazy",
		onerror: (e) => {
			e.currentTarget.src = PLACEHOLDER;
		},
	});

	// A small tag marks this row as a group (and shows how many it holds), the one
	// visual cue that sets it apart from a plain item row. The rest of the meta
	// line is a derived rollup of the members — a group has no status of its own.
	const summary = summarizeMembers(members);
	const total = members.length;
	const meta = el(
		"div.game-row__meta",
		{},
		el(
			"span.media-tag",
			{},
			`Group · ${total} item${total === 1 ? "" : "s"}`,
		),
	);
	if (total > 0 && summary.doneCount > 0) {
		const doneWord = statusLabel("done", labelMediaType(group)).toLowerCase();
		meta.append(
			el("span.group-stat", {}, `${summary.doneCount}/${total} ${doneWord}`),
		);
	}
	if (summary.avgRating != null) {
		meta.append(el("span.group-stat", {}, `${summary.avgRating.toFixed(1)}★`));
	}

	return el(
		"button.game-row.game-row--group",
		{ type: "button", onclick: onClick },
		cover,
		el(
			"div.game-row__body",
			{},
			el("div.game-row__title", {}, group.name),
			el("div.game-row__year", {}, groupSubtitle(members)),
			meta,
		),
	);
}
